const DB_NAME = 'PrepgrindOfflineDB';
const DB_VERSION = 3; // Incremented for test_cache_v2 to fix missing marks
const STORE_NAME = 'study_materials';
const TEST_CACHE_STORE = 'test_cache_v2';

export const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(TEST_CACHE_STORE)) {
        db.createObjectStore(TEST_CACHE_STORE, { keyPath: 'id' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const saveToOffline = async (material) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    // If it's a PDF, we might want to fetch and store the blob,
    // but for now, we store the metadata and assume user wants to cache text/html mainly.
    // If we want real offline PDF, we'd need to fetch(url) and store the blob.

    const request = store.put({
      ...material,
      offline_at: new Date().toISOString(),
    });

    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

export const getFromOffline = async (id) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const getAllOffline = async () => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const removeFromOffline = async (id) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

// ================= TEST CACHE LOGIC =================
// Caches test data for 24 hours
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

export const saveTestToCache = async (testId, data) => {
  try {
    const db = await initDB();
    return new Promise((resolve) => {
      const transaction = db.transaction(TEST_CACHE_STORE, 'readwrite');
      const store = transaction.objectStore(TEST_CACHE_STORE);

      const request = store.put({
        id: testId,
        data: data,
        timestamp: Date.now(),
      });

      request.onsuccess = () => resolve(true);
      request.onerror = () => resolve(false);
    });
  } catch (err) {
    console.warn('Failed to save to test cache:', err);
    return false;
  }
};

export const getTestFromCache = async (testId) => {
  try {
    const db = await initDB();
    return new Promise((resolve) => {
      const transaction = db.transaction(TEST_CACHE_STORE, 'readonly');
      const store = transaction.objectStore(TEST_CACHE_STORE);
      const request = store.get(testId);

      request.onsuccess = () => {
        const result = request.result;
        if (result && result.timestamp) {
          const now = Date.now();
          if (now - result.timestamp < CACHE_TTL_MS) {
            resolve(result.data);
          } else {
            // Expired
            resolve(null);
          }
        } else {
          resolve(null);
        }
      };
      request.onerror = () => resolve(null);
    });
  } catch (err) {
    console.warn('Failed to retrieve from test cache:', err);
    return null;
  }
};
